import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff } from 'lucide-react';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(username, password);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    background: '#13131f',
    border: '1px solid #1c1c2e',
    borderRadius: 10,
    color: '#f0f0ff',
    fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    outline: 'none',
    boxSizing: 'border-box' as const,
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#080810', fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=Cabinet+Grotesk:wght@800;900&display=swap" rel="stylesheet" />
      <div style={{ background: '#0e0e1a', border: '1px solid #1c1c2e', borderRadius: 20, padding: '48px 40px', width: 400, maxWidth: '90vw' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: 32, fontWeight: 900, color: '#f0f0ff', letterSpacing: -1 }}>FUTURESAINTS</div>
          <div style={{ fontSize: 12, color: '#4a4a6a', marginTop: 6, letterSpacing: 3, textTransform: 'uppercase' }}>Operations Hub</div>
        </div>
        <form onSubmit={handleLogin} autoComplete="off">
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: '#4a4a6a', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 8 }}>Username</label>
            <input
              value={username}
              onChange={e => setUsername(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              name="tg-user"
              style={inputStyle}
              placeholder="Enter username"
            />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontSize: 12, color: '#4a4a6a', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 8 }}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="new-password"
                name="tg-pass"
                style={{ ...inputStyle, paddingRight: 46 }}
                placeholder="Enter password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#4a4a6a',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={e => (e.currentTarget.style.color = '#8a8aaa')}
                onMouseLeave={e => (e.currentTarget.style.color = '#4a4a6a')}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          {error && <div style={{ background: '#2a1515', border: '1px solid #3a1515', borderRadius: 8, padding: '10px 14px', color: '#f87171', fontSize: 13, marginBottom: 16 }}>{error}</div>}
          <button type="submit" disabled={loading} style={{ width: '100%', padding: '13px', background: 'linear-gradient(135deg,#6e8efb,#a78bfa)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>

      {/* Override browser autofill styling */}
      <style>{`
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px #13131f inset !important;
          -webkit-text-fill-color: #f0f0ff !important;
          border-color: #1c1c2e !important;
          transition: background-color 5000s ease-in-out 0s;
          font-family: 'DM Sans', sans-serif !important;
        }
      `}</style>
    </div>
  );
};

export default Login;
